class TootoCategoryMarquee extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-tooto-category-marquee-track]');
    this.group = this.querySelector('[data-tooto-category-marquee-group]');
    if (!this.track || !this.group) return;

    this.speed = Number(this.dataset.speed || 28);
    this.pauseOnHover = this.dataset.pauseOnHover === 'true';
    this.offset = 0;
    this.groupWidth = 0;
    this.lastTime = 0;
    this.isPointerDown = false;
    this.isHovering = false;
    this.prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.staticMobileQuery = window.matchMedia('(max-width: 749px)');

    this.resizeObserver = new ResizeObserver(() => this.measure());
    this.resizeObserver.observe(this.group);
    this.measure();

    this.addEventListener('pointerdown', this.handlePointerDown);
    this.addEventListener('pointermove', this.handlePointerMove);
    this.addEventListener('pointerup', this.handlePointerUp);
    this.addEventListener('pointercancel', this.handlePointerUp);
    this.addEventListener('pointerleave', this.handlePointerLeave);
    this.addEventListener('mouseenter', this.handleMouseEnter);
    this.addEventListener('mouseleave', this.handleMouseLeave);
    this.addEventListener('dragstart', this.handleDragStart);
    this.addEventListener('click', this.handleClick, true);
    this.staticMobileQuery.addEventListener?.('change', this.handleModeChange);

    this.animationFrame = requestAnimationFrame(this.tick);
  }

  disconnectedCallback() {
    this.resizeObserver?.disconnect();
    cancelAnimationFrame(this.animationFrame);
    this.removeEventListener('pointerdown', this.handlePointerDown);
    this.removeEventListener('pointermove', this.handlePointerMove);
    this.removeEventListener('pointerup', this.handlePointerUp);
    this.removeEventListener('pointercancel', this.handlePointerUp);
    this.removeEventListener('pointerleave', this.handlePointerLeave);
    this.removeEventListener('mouseenter', this.handleMouseEnter);
    this.removeEventListener('mouseleave', this.handleMouseLeave);
    this.removeEventListener('dragstart', this.handleDragStart);
    this.removeEventListener('click', this.handleClick, true);
    this.staticMobileQuery?.removeEventListener?.('change', this.handleModeChange);
  }

  measure() {
    if (this.staticMobileQuery?.matches) {
      this.offset = 0;
      this.track.style.transform = 'none';
      return;
    }

    this.groupWidth = this.group.getBoundingClientRect().width;
    this.normalizeOffset();
    this.applyTransform();
  }

  normalizeOffset() {
    if (!this.groupWidth) return;

    while (this.offset <= -this.groupWidth) this.offset += this.groupWidth;
    while (this.offset > 0) this.offset -= this.groupWidth;
  }

  applyTransform() {
    this.track.style.transform = `translate3d(${this.offset}px, 0, 0)`;
  }

  tick = (time) => {
    if (!this.lastTime) this.lastTime = time;
    const delta = time - this.lastTime;
    this.lastTime = time;

    const isPaused =
      this.staticMobileQuery?.matches ||
      this.prefersReducedMotion ||
      this.isPointerDown ||
      (this.pauseOnHover && this.isHovering);

    if (!isPaused && this.groupWidth) {
      this.offset -= (this.speed * delta) / 1000;
      this.normalizeOffset();
      this.applyTransform();
    }

    this.animationFrame = requestAnimationFrame(this.tick);
  };

  handlePointerDown = (event) => {
    if (this.staticMobileQuery?.matches) return;
    if (event.button !== undefined && event.button !== 0) return;
    this.isPointerDown = true;
    this.hasDragged = false;
    this.pointerStartX = event.clientX;
    this.offsetStart = this.offset;
    this.classList.add('is-dragging');
    this.setPointerCapture(event.pointerId);
  };

  handlePointerMove = (event) => {
    if (!this.isPointerDown) return;
    event.preventDefault();
    if (Math.abs(event.clientX - this.pointerStartX) > 4) this.hasDragged = true;
    this.offset = this.offsetStart + event.clientX - this.pointerStartX;
    this.normalizeOffset();
    this.applyTransform();
  };

  handlePointerUp = (event) => {
    if (!this.isPointerDown) return;
    this.isPointerDown = false;
    this.classList.remove('is-dragging');
    if (this.hasPointerCapture(event.pointerId)) this.releasePointerCapture(event.pointerId);
  };

  handlePointerLeave = (event) => {
    if (this.isPointerDown) this.handlePointerUp(event);
  };

  handleMouseEnter = () => {
    this.isHovering = true;
  };

  handleMouseLeave = () => {
    this.isHovering = false;
  };

  handleDragStart = (event) => {
    event.preventDefault();
  };

  handleClick = (event) => {
    if (!this.hasDragged) return;
    event.preventDefault();
    event.stopPropagation();
    this.hasDragged = false;
  };

  handleModeChange = () => {
    this.measure();
  };
}

if (!customElements.get('tooto-category-marquee')) {
  customElements.define('tooto-category-marquee', TootoCategoryMarquee);
}
